/**
 * Mission Card Component
 * Displays individual mission details in mission history
 */

import React from 'react';
import { ArrowDown, ArrowUp, Eye, RefreshCw } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { 
  getMissionStatusColor, 
  getHealthColor, 
  getWearColor,
  formatMissionDuration 
} from '../utils/missionHistoryUtils';

const MissionCard = ({ mission, onViewDetails, isMetric = true }) => {
  const { getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();

  const statusColors = getMissionStatusColor(mission.status);
  const healthBefore = mission.healthChange?.before?.overall || 0;
  const healthAfter = mission.healthChange?.after?.overall || 0;
  const healthChange = healthAfter - healthBefore;

  // Convert distance based on unit system
  const convertDistance = (km) => {
    if (isMetric) return `${km.toFixed(1)} km`;
    return `${(km * 0.621371).toFixed(1)} mi`;
  };

  // Format date
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate health bar width
  const healthBarWidth = `${healthAfter}%`;

  return (
    <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-5 hover:${themeClasses.secondary} transition-all`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3 gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-blue-400 text-lg flex-shrink-0">●</span>
            <h4 className={`${themeClasses.textPrimary} font-semibold text-base truncate min-w-0`}>
              {mission.route?.start || 'Unknown'} → {mission.route?.destination || 'Unknown'}
            </h4>
          </div>
          <p className={`${themeClasses.textMuted} text-sm truncate`}>
            {mission.missionName}
          </p>
        </div>
        <span className={`${themeClasses.textMuted} text-sm whitespace-nowrap flex-shrink-0`}>
          {formatDate(mission.timestamp)}
        </span>
      </div>

      {/* Mission Info Row */}
      <div className="flex items-center gap-4 mb-4 text-sm flex-wrap">
        <span className={themeClasses.textPrimary}>
          <span className={themeClasses.textMuted}>Distance:</span> {convertDistance(mission.distance || 0)}
        </span>
        <span className={themeClasses.textPrimary}>
          <span className={themeClasses.textMuted}>Duration:</span> {formatMissionDuration(mission.duration)}
        </span>
        <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors.bg} ${statusColors.text} border ${statusColors.border}`}>
          {statusColors.icon} {mission.status}
        </span>
      </div>

      {/* Health Impact */}
      <div className="mb-4">
        <p className={`${themeClasses.textMuted} text-sm mb-2`}>Health Impact:</p>
        <div className="relative">
          {/* Background bar */}
          <div className={`w-full h-6 ${themeClasses.secondary} rounded-full overflow-hidden`}>
            {/* Health bar */}
            <div 
              className={`h-full transition-all duration-500 ${
                healthAfter >= 80 ? 'bg-green-500' :
                healthAfter >= 60 ? 'bg-yellow-500' :
                healthAfter >= 40 ? 'bg-orange-500' :
                'bg-red-500'
              }`}
              style={{ width: healthBarWidth }}
            ></div>
          </div>
          {/* Health text */}
          <div className="flex items-center justify-between mt-2 text-sm">
            <span className={`${getHealthColor(healthBefore)} font-semibold`}>
              {healthBefore}%
            </span>
            <div className="flex items-center gap-1">
              {healthChange < 0 ? (
                <ArrowDown size={14} className="text-red-400" />
              ) : healthChange > 0 ? (
                <ArrowUp size={14} className="text-green-400" />
              ) : null}
              <span className={`font-semibold ${
                healthChange < 0 ? 'text-red-400' : 
                healthChange > 0 ? 'text-green-400' : 
                themeClasses.textMuted
              }`}>
                {healthChange > 0 ? '+' : ''}{healthChange}%
              </span>
            </div>
            <span className={`${getHealthColor(healthAfter)} font-semibold`}>
              {healthAfter}%
            </span>
          </div>
        </div>
      </div>

      {/* Component Wear Grid */}
      <div className="mb-4">
        <p className={`${themeClasses.textMuted} text-sm mb-2`}>Component Wear:</p>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className={`${themeClasses.secondary} rounded p-2`}>
            <span className={themeClasses.textMuted}>Engine:</span>{' '}
            <span className={`font-semibold ${getWearColor(mission.componentWear?.engine || mission.wear?.engine || 0)}`}>
              -{mission.componentWear?.engine || mission.wear?.engine || 0}%
            </span>
          </div>
          <div className={`${themeClasses.secondary} rounded p-2`}>
            <span className={themeClasses.textMuted}>Brakes:</span>{' '}
            <span className={`font-semibold ${getWearColor(mission.componentWear?.brakes || mission.wear?.brakes || 0)}`}>
              -{mission.componentWear?.brakes || mission.wear?.brakes || 0}%
            </span>
          </div>
          <div className={`${themeClasses.secondary} rounded p-2`}>
            <span className={themeClasses.textMuted}>Suspension:</span>{' '}
            <span className={`font-semibold ${getWearColor(mission.componentWear?.suspension || mission.wear?.suspension || 0)}`}>
              -{mission.componentWear?.suspension || mission.wear?.suspension || 0}%
            </span>
          </div>
          <div className={`${themeClasses.secondary} rounded p-2`}>
            <span className={themeClasses.textMuted}>Transmission:</span>{' '}
            <span className={`font-semibold ${getWearColor(mission.componentWear?.transmission || mission.wear?.transmission || 0)}`}>
              -{mission.componentWear?.transmission || mission.wear?.transmission || 0}%
            </span>
          </div>
        </div>
      </div>

      {/* Additional Info */}
      {mission.reroutes > 0 && (
        <div className="flex items-center justify-center mb-4 text-sm">
          <span className="flex items-center gap-1 text-amber-400">
            <RefreshCw size={14} />
            {mission.reroutes} reroute{mission.reroutes > 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Enhanced Reroute Breakdown (for completed missions with reroutes) */}
      {mission.status === 'completed' && mission.reroutes > 0 && mission.actualData && (
        <div className={`${themeClasses.secondary} border-2 border-amber-500/30 rounded-lg p-4 mb-4`}>
          <div className="flex items-center gap-2 mb-4">
            <RefreshCw size={16} className="text-amber-400" />
            <h5 className={`${themeClasses.textPrimary} font-semibold text-sm`}>Rerouted Mission Details</h5>
          </div>
          
          {/* Route 1 (Initial) */}
          <div className="mb-3 pb-3 border-b border-gray-700/50">
            <div className="flex items-center gap-1 mb-2">
              <span className="text-blue-400 text-xs">📍</span>
              <p className={`${themeClasses.textPrimary} font-semibold text-xs`}>ROUTE 1 (Initial)</p>
            </div>
            <div className={`${themeClasses.card} rounded p-2 space-y-1 text-xs`}>
              <div className="flex justify-between">
                <span className={themeClasses.textMuted}>Route:</span>
                <span className={`${themeClasses.textPrimary} font-medium`}>
                  {mission.route?.start} → {mission.route?.destination}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textMuted}>Planned:</span>
                <span className={themeClasses.textPrimary}>{convertDistance(mission.actualData.plannedDistance)}</span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textMuted}>Traveled:</span>
                <span className={`${themeClasses.textPrimary} font-medium`}>
                  {convertDistance(mission.actualData.distanceToReroutePoint || 0)}
                  <span className="text-amber-400 ml-1">
                    ({Math.round((mission.actualData.distanceToReroutePoint / mission.actualData.plannedDistance) * 100)}%)
                  </span>
                </span>
              </div>
              <div className="flex justify-between pt-1 border-t border-gray-700/30">
                <span className={themeClasses.textMuted}>Health:</span>
                <span className={themeClasses.textPrimary}>
                  {healthBefore}% → {Math.round(healthBefore - ((healthBefore - healthAfter) * (mission.actualData.distanceToReroutePoint / mission.actualData.actualDistance)))}%
                </span>
              </div>
            </div>
          </div>

          {/* Route 2 (Rerouted) */}
          <div className="mb-3 pb-3 border-b border-gray-700/50">
            <div className="flex items-center gap-1 mb-2">
              <span className="text-amber-400 text-xs">🔄</span>
              <p className={`${themeClasses.textPrimary} font-semibold text-xs`}>ROUTE 2 (Rerouted)</p>
            </div>
            <div className={`${themeClasses.card} rounded p-2 space-y-1 text-xs`}>
              <div className="flex justify-between">
                <span className={themeClasses.textMuted}>Distance:</span>
                <span className={`${themeClasses.textPrimary} font-medium`}>
                  {convertDistance(mission.actualData.newRouteDistance || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textMuted}>Traveled:</span>
                <span className={`${themeClasses.textPrimary} font-medium`}>
                  {convertDistance(mission.actualData.newRouteDistance || 0)}
                  <span className="text-green-400 ml-1">(100%)</span>
                </span>
              </div>
              <div className="flex justify-between pt-1 border-t border-gray-700/30">
                <span className={themeClasses.textMuted}>Health:</span>
                <span className={themeClasses.textPrimary}>
                  {Math.round(healthBefore - ((healthBefore - healthAfter) * (mission.actualData.distanceToReroutePoint / mission.actualData.actualDistance)))}% → {healthAfter}%
                </span>
              </div>
            </div>
          </div>

          {/* Final Summary */}
          <div className="bg-gradient-to-br from-blue-600/10 to-purple-600/10 rounded p-3">
            <div className="flex items-center gap-1 mb-2">
              <span className="text-purple-400 text-xs">📊</span>
              <p className={`${themeClasses.textPrimary} font-semibold text-xs`}>FINAL SUMMARY</p>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className={themeClasses.textMuted}>Total Distance:</span>
                <span className={`${themeClasses.textPrimary} font-bold`}>
                  {convertDistance(mission.actualData.actualDistance)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textMuted}>Variance:</span>
                <span className={`font-bold ${
                  mission.actualData.distanceVariance > 0 ? 'text-amber-400' : 
                  mission.actualData.distanceVariance < 0 ? 'text-green-400' : 
                  themeClasses.textPrimary
                }`}>
                  {mission.actualData.distanceVariance > 0 ? '+' : ''}
                  {convertDistance(Math.abs(mission.actualData.distanceVariance))}
                  <span className={themeClasses.textMuted}>
                    {' '}({mission.actualData.distanceVariance > 0 ? '+' : ''}{((mission.actualData.distanceVariance / mission.actualData.plannedDistance) * 100).toFixed(1)}%)
                  </span>
                </span>
              </div>
              <div className="flex justify-between pt-1 border-t border-gray-700/30">
                <span className={themeClasses.textMuted}>Final Health:</span>
                <span className={`${themeClasses.textPrimary} font-bold`}>
                  {healthBefore}% → {healthAfter}%
                  <span className="text-red-400 ml-1">({healthChange}%)</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Details Button */}
      <button
        onClick={() => onViewDetails && onViewDetails(mission)}
        className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 text-sm font-medium"
      >
        <Eye size={16} />
        View Full Details
      </button>
    </div>
  );
};

export default MissionCard;
